// The "Is the plan working?" chart-switcher — dropdown (top-right of the
// header) + the 2 charts it toggles between. Extracted out of SummaryHero.js
// (already owns TDEE modal orchestration, the toast, and the TDEE/suggested-
// intake cards) so that file stays focused on that, not chart selection too.
//
// Bodyweight used to be a 3rd mode here, but it shared this same
// dateFrom/dateTo-scoped `series` as the other two — a coach narrowing the
// nutrition period to 1-2 weeks (the normal case) got a near-useless 1-2
// week bodyweight trend too. Moved to its own standalone section
// (BodyweightTrendCard.js, rendered below this one in SummaryView.js) with
// its own quick-range control, always anchored to real "today" — per
// product lead feedback.
//
// The 2 remaining charts either already existed (HeroChart) or wrap an
// existing calculation function (ReportsMacroChart's own
// buildMacroBreakdownSeries) — no new data/math here, just which one is
// currently mounted. series/macroSeries/expenditure are already computed
// once up in SummaryView.js, so switching is an instant, cheap remount, not
// a recompute.

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../../parts/SvgIcon";
import Dropdown from "../../../../../parts/dropdown";
import { HeroChart } from "./SummaryHeroChart";
import { ReportsMacroChart } from "./ReportsMacroChart";

const CHART_MODES = ["calories", "macros"];

const h = (t, key, opts) => t(`nutritionPage.progress.hero.${key}`, opts);

export const SummaryHeroChartSwitcher = ({ series, macroSeries = [], phaseAreas, expenditure, units }) => {
  const { t } = useTranslation("athlete-portal");

  // Local state, not URL-synced — same convention as SummaryHero's own
  // modal-open flags.
  const [chartMode, setChartMode] = useState("calories");

  // Only the charts need these (HeroChart's own TDEE line, and
  // ReportsMacroChart's optional one) — undefined/false/null when the
  // estimate isn't confident yet, same gating SummaryHero's other TDEE
  // displays use.
  const decoupled = expenditure.confident ? expenditure.decoupled : false;
  const staleData = expenditure.confident ? expenditure.staleData : false;
  const weeklyChange = expenditure.confident ? expenditure.weeklyChange : null;

  return (
    <>
      <div className="np-hero__header">
        <SvgIcon
          src="/img/new-icons/activity-line.svg"
          useCurrentColor
          classNames="np-hero__header-icon"
        />
        <span className="np-hero__title">{h(t, "title")}</span>
        <div className="np-hero__chart-switcher">
          <Dropdown
            summaryText={
              <span className="inline-flex items-center gap-1.5">
                <SvgIcon
                  src="/img/new-icons/line-chart-trend.svg"
                  useCurrentColor
                  classNames="h-3 w-4 shrink-0"
                />
                {h(t, `chartSwitcher.${chartMode}`)}
              </span>
            }
            options={CHART_MODES.map((mode) => ({ text: h(t, `chartSwitcher.${mode}`), value: mode }))}
            onSelectFn={setChartMode}
            selectedValue={chartMode}
            alignRight
          />
        </div>
      </div>
      <p className="np-hero__subtitle">
        {h(t, "subtitle")}
      </p>

      {chartMode === "calories" && (
        <HeroChart
          series={series}
          phaseAreas={phaseAreas}
          workingExpenditure={expenditure.workingExpenditure}
          decoupled={decoupled}
          staleData={staleData}
          weeklyChange={weeklyChange}
          units={units}
        />
      )}
      {chartMode === "macros" && (
        <ReportsMacroChart
          series={macroSeries}
          workingExpenditure={expenditure.workingExpenditure}
          decoupled={decoupled}
          staleData={staleData}
          weeklyChange={weeklyChange}
          flush
        />
      )}
    </>
  );
};

SummaryHeroChartSwitcher.propTypes = {
  series: PropTypes.array.isRequired,
  macroSeries: PropTypes.array,
  phaseAreas: PropTypes.array.isRequired,
  expenditure: PropTypes.object.isRequired,
  units: PropTypes.string,
};
